import { useToast } from '@vtex/admin-ui'
import { useFormState } from '@vtex/admin-ui-form'
import { useAdmin, useNavigation } from '@vtex/raccoon-next'
import { hasBudgetIds, useBudgetEntity, usePromotionMutation } from 'hooks'
import { useSearchParams } from 'next/navigation'
import { useIntl } from 'react-intl'
import { catchLogger, getRnbErrorMessageId } from 'shared'
import type { RnbCalculatorPayload } from 'typings'
import { useAnalytics } from 'use-analytics'
import { toAPIPromotion } from '../components/Form/mappers/toAPI'
import type { Promotion } from '../components/typings'
import {
  buildPromotionEventData,
  getPromotionOrigin,
  ORIGIN_EVENT_MAP,
} from '../utils/promotionEvent'
import { useIntercom } from './useIntercom'
export function useSavePromotion() {
  const {
    formState: { submitCount },
  } = useFormState()
  const { $t } = useIntl()
  const { updateLinkedEntity } = useBudgetEntity()
  const { save: mutate } = usePromotionMutation()
  const { navigate } = useNavigation()
  const { track } = useAnalytics()
  const { trackIntercomEvent } = useIntercom()
  const admin = useAdmin()

  const searchParams = useSearchParams()

  const showToast = useToast()

  type Props = {
    data: Promotion
    originalValues: RnbCalculatorPayload | undefined
    initialPromotion: Promotion
  }

  const sendEvent = (props: Pick<Props, 'data' | 'initialPromotion'>) => {
    const { data, initialPromotion } = props
    const origin = getPromotionOrigin(searchParams)

    const eventData = buildPromotionEventData({
      data,
      initialPromotion,
      attempt: submitCount + 1,
      origin,
      searchParams,
    })

    track(ORIGIN_EVENT_MAP[origin], eventData)
    trackIntercomEvent('Promotion Saved', { type: data.type })
  }

  async function save(props: Props) {
    const { data, originalValues, initialPromotion } = props

    return new Promise<void>((resolve, reject) => {
      const convertedData = toAPIPromotion(data, originalValues)

      mutate(
        {
          data: catchLogger({
            id: 'to-api-promotion',
            fn: () => convertedData,
            admin,
            onError: () => {
              showToast({
                message: $t({
                  id: 'admin/promotions.form.save.error',
                }),
                variant: 'critical',
                key: 'to-api-error-notification-toast',
              })
            },
          })!,
        },
        {
          onSuccess: async (payload: RnbCalculatorPayload) => {

            resolve()

            showToast({
              message: $t({
                id: 'admin/promotions.form.save.success',
              }),
              variant: 'positive',
              key: 'save-success-notification-toast',
            })

            await catchLogger({
              id: 'update-budget-linked-entity',
              fn: async () => {
                const budget = data.restrictions?.budget

                if (!(originalValues === undefined && hasBudgetIds(budget))) {
                  return
                }

                const { idCalculatorConfiguration } = payload

                if (!idCalculatorConfiguration) {
                  return
                }

                const linkParams = {
                  budgetId: budget.id,
                  allocationId: budget.allocationId,
                  linkedEntityId: idCalculatorConfiguration,
                }

                try {
                  return await updateLinkedEntity(linkParams)
                } catch (err) {
                  const cause = err instanceof Error ? err : new Error(String(err))

                  throw new Error(
                    `Failed to update budget linked entity: ${cause.message}`,
                    { cause: { payload: linkParams, error: cause } }
                  )
                }
              },
              admin,
              throwOnError: false,
              onError: () => {
                showToast({
                  message: $t({
                    id: 'admin/promotions.form.save.budget-link.error',
                  }),
                  variant: 'warning',
                  key: 'save-linked-entity-warning-notification-toast',
                })
              },
            })

            catchLogger({
              id: 'send-save-event',
              fn: () =>
                sendEvent({
                  data,
                  initialPromotion,
                }),
              admin,
              throwOnError: false,
            })

            navigate('/')
          },
          onError: (error: unknown) => {
            reject()

            const messageId = getRnbErrorMessageId(error)

            if (messageId) {
              showToast({
                message: $t({ id: messageId }),
                variant: 'critical',
                key: 'save-error-price-table-limit-toast',
              })

              return
            }

            showToast({
              message: $t({
                id: 'admin/promotions.form.save.error',
              }),
              variant: 'critical',
              action: {
                label: $t({
                  id: 'admin/promotions.form.utils.try-again',
                }),
                onClick: () =>
                  save({
                    data,
                    originalValues,
                    initialPromotion,
                  }),
              },
              key: 'save-error-notification-toast',
            })
          },
        }
      )
    })
  }

  return {
    save,
  }
}
